import { Button as HexaButton } from '@abi-labs-hexa/web-v2/components/button';
import { useMsal } from '@azure/msal-react';
import {
  Badge,
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Select as ChakraReactSelect } from 'chakra-react-select';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import type { GroupBase, MultiValueProps } from 'react-select';
import { z } from 'zod';

import getSelectedAccount from 'watchtower-ui/api/msalAccount';
import wtDefaults from 'watchtower-ui/mockJson/wt_defaults.json';
import 'watchtower-ui/locales/i18n';
import buttonStyles from 'watchtower-ui/styles/common/button.module.scss';

interface AccessRequestFormProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SelectOption {
  label: string;
  value: string;
}

const accessRequestSchema = z.object({
  managerEmail: z
    .string()
    .min(1, 'Manager email is required')
    .regex(
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
      'Invalid email format',
    ),
  employeeType: z.enum(['AB Employee', 'Contractor', 'External']),
  selectedBrands: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .min(1, 'At least one brand must be selected')
    .refine(
      (brands) => {
        if (brands.some((b) => b.value === 'ALL')) {
          return (
            brands.length === 1 || brands.every((b) => b.value === 'ALL' || !brands.find((bb) => bb.value === 'ALL'))
          );
        }
        return true;
      },
      {
        message: 'When ALL is selected, no other brands can be selected',
      },
    ),
  selectedRegions: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .min(1, 'At least one region must be selected')
    .refine(
      (regions) => {
        if (regions.some((r) => r.value === 'ALL')) {
          return (
            regions.length === 1 || regions.every((r) => r.value === 'ALL' || !regions.find((rr) => rr.value === 'ALL'))
          );
        }
        return true;
      },
      {
        message: 'When ALL is selected, no other regions can be selected',
      },
    ),
  selectedInvestmentTypes: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .min(1, 'At least one investment type must be selected')
    .refine(
      (types) => {
        if (types.some((t) => t.value === 'ALL')) {
          return types.length === 1 || types.every((t) => t.value === 'ALL' || !types.find((tt) => tt.value === 'ALL'));
        }
        return true;
      },
      {
        message: 'When ALL is selected, no other investment types can be selected',
      },
    ),
  needsNonInvestmentVehicles: z.boolean(),
});

type FormData = z.infer<typeof accessRequestSchema>;

interface MultiSelectProps {
  label: string;
  placeholder: string;
  options: SelectOption[];
  value: SelectOption[];
  onChange: (values: SelectOption[]) => void;
  colorScheme: 'blue' | 'green' | 'orange';
}

interface SelectState {
  data: { value: string };
  isSelected: boolean;
}

interface StylesConfig {
  [key: string]: (provided: Record<string, unknown>, state?: SelectState) => Record<string, unknown>;
}

const CustomMultiValue = <Option extends SelectOption, IsMulti extends boolean>(
  props: MultiValueProps<Option, IsMulti, GroupBase<Option>> & {
    value: SelectOption[];
  },
) => {
  const { children, value, data } = props;
  const hasAllSelected = value.some((item) => item.value === 'ALL');
  const isCurrentOptionAll = data?.value === 'ALL';

  if (hasAllSelected) {
    return isCurrentOptionAll ? <components.MultiValue {...props}>{children}</components.MultiValue> : null;
  }
  return <components.MultiValue {...props}>{children}</components.MultiValue>;
};

const createCustomMultiValue = (currentValue: SelectOption[]) => {
  const MultiValue = <Option extends SelectOption, IsMulti extends boolean>(
    props: MultiValueProps<Option, IsMulti, GroupBase<Option>>,
  ) => <CustomMultiValue {...props} value={currentValue} />;

  return MultiValue;
};

const getChakraStyles = (colorScheme: 'blue' | 'green' | 'orange'): StylesConfig => ({
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    bg: 'gray.50',
    border: '1px',
    borderColor: 'gray.200',
    borderRadius: 'md',
    minHeight: '40px',
    _hover: { borderColor: 'blue.300' },
    _focus: { borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' },
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: '2px 8px',
    gap: '4px',
    flexWrap: 'wrap',
  }),
  multiValue: (provided: Record<string, unknown>) => ({
    ...provided,
    bg: 'transparent',
    border: '1px solid',
    borderColor: `${colorScheme}.300`,
    borderRadius: 'md',
    margin: '1px',
  }),
  multiValueLabel: (provided: Record<string, unknown>) => ({
    ...provided,
    color: `${colorScheme}.700`,
    fontSize: 'sm',
    padding: '2px 6px',
    bg: 'transparent',
  }),
  multiValueRemove: (provided: Record<string, unknown>) => ({
    ...provided,
    color: `${colorScheme}.600`,
    bg: 'transparent',
    _hover: { bg: `${colorScheme}.100`, color: `${colorScheme}.800` },
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: 9999,
    position: 'absolute',
  }),
  menuList: (provided: Record<string, unknown>) => ({
    ...provided,
    maxHeight: '200px',
    overflowY: 'auto',
  }),
  option: (provided: Record<string, unknown>, state?: SelectState) => {
    const isAllOption = state?.data?.value === 'ALL';
    const isSelected = state?.isSelected || false;

    let backgroundColor = provided.bg as string;
    if (isAllOption) {
      backgroundColor = isSelected ? `${colorScheme}.500` : `${colorScheme}.50`;
    }

    return {
      ...provided,
      bg: backgroundColor,
      fontWeight: isAllOption ? 'bold' : 'normal',
      borderBottom: isAllOption ? '1px solid' : 'none',
      borderColor: `${colorScheme}.200`,
    };
  },
});
const MultiSelectField: FC<MultiSelectProps> = ({ label, placeholder, options, value, onChange, colorScheme }) => {
  const { t } = useTranslation();

  const customComponents = useMemo(
    () => ({
      MultiValue: createCustomMultiValue(value),
    }),
    [value],
  );

  return (
    <FormControl>
      <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" top="-15px">
        {label}
        <Badge ml={2} colorScheme={colorScheme} variant="subtle" fontSize="xs">
          {t('common:multiSelect')}
        </Badge>
      </FormLabel>
      <ChakraReactSelect
        isMulti
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={(values) => onChange((values as SelectOption[]) ?? [])}
        components={customComponents}
        chakraStyles={getChakraStyles(colorScheme)}
        selectedOptionStyle="check"
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
      />
    </FormControl>
  );
};

const AccessRequestForm: FC<AccessRequestFormProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const msalDetails = useMsal();
  const toast = useToast();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(accessRequestSchema),
    defaultValues: {
      managerEmail: '',
      employeeType: 'AB Employee',
      selectedBrands: [],
      selectedRegions: [],
      selectedInvestmentTypes: [],
      needsNonInvestmentVehicles: false,
    },
  });

  const baseBrandOptions: SelectOption[] = wtDefaults.brand.map((brand) => ({
    label: brand,
    value: brand,
  }));
  const brandOptions: SelectOption[] = [{ label: 'ALL', value: 'ALL' }, ...baseBrandOptions];

  const baseRegionOptions: SelectOption[] = wtDefaults.country.USA.region.map((region) => ({
    label: region,
    value: region,
  }));
  const regionOptions: SelectOption[] = [{ label: 'ALL', value: 'ALL' }, ...baseRegionOptions];

  const baseInvestmentTypeOptions: SelectOption[] = wtDefaults.Investment_signals.map((investmentType) => ({
    label: investmentType,
    value: investmentType,
  }));
  const investmentTypeOptions: SelectOption[] = [{ label: 'ALL', value: 'ALL' }, ...baseInvestmentTypeOptions];

  const handleSelectChange = (field: keyof FormData, values: SelectOption[]) => {
    const fieldMap = {
      selectedBrands: { baseOptions: baseBrandOptions },
      selectedRegions: { baseOptions: baseRegionOptions },
      selectedInvestmentTypes: { baseOptions: baseInvestmentTypeOptions },
    };

    const { baseOptions } = fieldMap[field as keyof typeof fieldMap];
    const hasAll = values.some((v) => v.value === 'ALL');
    const currentValues = getValues(field) as SelectOption[];
    const previouslyHadAll = currentValues.some((v) => v.value === 'ALL');

    let newValues: SelectOption[];
    if (hasAll) {
      newValues = [{ label: 'ALL', value: 'ALL' }, ...baseOptions];
    } else if (previouslyHadAll && !hasAll) {
      newValues = [];
    } else {
      newValues = values;
    }

    setValue(field, newValues);
  };

  const onSubmit = (data: FormData) => {
    try {
      const user = getSelectedAccount(msalDetails);
      const userEmail = user?.username ?? user?.homeAccountId ?? 'Unknown user';
      const userName = user?.name ?? userEmail;
      const emailSubject = encodeURIComponent(t('common:emailTemplate.emailSubjectPrefix') + userName);

      const formatSelections = (selections: SelectOption[]) => {
        if (selections.length === 0) {
          return t('common:noneSpecified');
        }

        if (selections[0]?.value === 'ALL') {
          return t('common:all');
        }

        return selections.map((s) => s.label).join(', ');
      };

      const accessDetails = `
${t('common:emailTemplate.accessRequestDetails')}
${t('common:emailTemplate.separator')}

${t('common:emailTemplate.userInformation')}
- ${t('common:emailTemplate.name')}: ${userName}
- ${t('common:emailTemplate.email')}: ${userEmail}
- ${t('common:emailTemplate.employeeType')}: ${data.employeeType}
- ${t('common:emailTemplate.managerEmail')}: ${data.managerEmail}
- ${t('common:emailTemplate.requestDate')}: ${new Date().toLocaleString()}

${t('common:emailTemplate.requestedAccess')}

${t('common:emailTemplate.brands')}: ${formatSelections(data.selectedBrands)}
${t('common:emailTemplate.regions')}: ${formatSelections(data.selectedRegions)}
${t('common:emailTemplate.investmentTypes')}: ${formatSelections(data.selectedInvestmentTypes)}
${t('common:emailTemplate.nonInvestmentVehicles')}: ${data.needsNonInvestmentVehicles ? t('common:emailTemplate.yes') : t('common:emailTemplate.no')}

${t('common:emailTemplate.separator')}

${t('common:emailTemplate.reviewMessage')}

${t('common:emailTemplate.thankYou')}
${t('common:emailTemplate.systemSignature')}`;

      const emailBody = encodeURIComponent(accessDetails);
      const adminEmail = (import.meta.env.VITE_ADMIN_EMAIL as string) ?? 'wt-user-access@ab-inbev.com';
      const mailtoLink = `mailto:${adminEmail}?cc=${encodeURIComponent(data.managerEmail)}&subject=${emailSubject}&body=${emailBody}`;

      window.open(mailtoLink, '_blank', 'noopener,noreferrer');

      toast({
        title: t('common:accessRequestSubmitted'),
        description: t('common:emailClientDescription'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error('Failed to generate access request email:', error);
      toast({
        title: t('common:error'),
        description: t('common:accessRequestFailure'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFormSubmit = () => {
    // eslint-disable-next-line promise/prefer-await-to-then -- Form submission errors are handled within onSubmit
    handleSubmit(onSubmit)().catch(() => {
      // Error handling is done in onSubmit function
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent maxHeight="90vh" bg="white" borderRadius="xl" shadow="2xl">
        <ModalHeader bg="blue.50" borderTopRadius="xl" py={6}>
          <VStack spacing={2} align="start">
            <Text fontSize="xl" fontWeight="bold" color="blue.900">
              {t('common:requestWatchTowerAccess')}
            </Text>
            <Text fontSize="sm" color="blue.600">
              {t('common:completeFormDescription')}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton top={6} right={6} />
        <ModalBody pb={6} px={8}>
          <VStack spacing={2} align="stretch">
            <Box>
              <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
                📋 {t('common:personalInformation')}
              </Text>
              <VStack spacing={4} align="stretch">
                <Controller
                  name="managerEmail"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl isRequired isInvalid={!!fieldState.error}>
                      <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" top="-10px">
                        {t('common:managerEmailAddress')}
                      </FormLabel>
                      <Input
                        {...field}
                        type="email"
                        placeholder={t('common:managerEmailPlaceholder')}
                        bg="gray.50"
                        border="1px"
                        borderColor={fieldState.error ? 'red.300' : 'gray.200'}
                        borderRadius="md"
                        _hover={{ borderColor: fieldState.error ? 'red.400' : 'blue.300' }}
                        _focus={{
                          borderColor: fieldState.error ? 'red.500' : 'blue.500',
                          boxShadow: fieldState.error ? '0 0 0 1px #E53E3E' : '0 0 0 1px #3182ce',
                        }}
                      />
                      <FormErrorMessage fontSize="xs">{fieldState.error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />

                <Controller
                  name="employeeType"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl isRequired isInvalid={!!fieldState.error}>
                      <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" top="-10px">
                        {t('common:employeeType')}
                      </FormLabel>
                      <Select
                        {...field}
                        placeholder={t('common:selectEmploymentType')}
                        bg="gray.50"
                        border="1px"
                        borderColor={fieldState.error ? 'red.300' : 'gray.200'}
                        borderRadius="md"
                        _hover={{ borderColor: fieldState.error ? 'red.400' : 'blue.300' }}
                        _focus={{
                          borderColor: fieldState.error ? 'red.500' : 'blue.500',
                          boxShadow: fieldState.error ? '0 0 0 1px #E53E3E' : '0 0 0 1px #3182ce',
                        }}
                      >
                        <option value="AB Employee">{t('common:abEmployee')}</option>
                        <option value="Contractor">{t('common:contractor')}</option>
                        <option value="External">{t('common:external')}</option>
                      </Select>
                      <FormErrorMessage fontSize="xs">{fieldState.error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />
              </VStack>
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
                🔐 {t('common:accessPermissions')}
              </Text>

              <VStack spacing={6} align="stretch">
                <Controller
                  name="selectedBrands"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl isRequired isInvalid={!!fieldState.error}>
                      <MultiSelectField
                        label={t('common:brandsAccess')}
                        placeholder={t('common:selectBrands')}
                        options={brandOptions}
                        value={field.value}
                        onChange={(values) => {
                          handleSelectChange('selectedBrands', values);
                          field.onChange(values);
                        }}
                        colorScheme="blue"
                      />
                      <FormErrorMessage fontSize="xs">{fieldState.error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />

                <Controller
                  name="selectedRegions"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl isRequired isInvalid={!!fieldState.error}>
                      <MultiSelectField
                        label={t('common:regionsAccess')}
                        placeholder={t('common:selectRegions')}
                        options={regionOptions}
                        value={field.value}
                        onChange={(values) => {
                          handleSelectChange('selectedRegions', values);
                          field.onChange(values);
                        }}
                        colorScheme="green"
                      />
                      <FormErrorMessage fontSize="xs">{fieldState.error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />

                <Controller
                  name="selectedInvestmentTypes"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl isRequired isInvalid={!!fieldState.error}>
                      <MultiSelectField
                        label={t('common:investmentTypesAccess')}
                        placeholder={t('common:selectInvestmentTypes')}
                        options={investmentTypeOptions}
                        value={field.value}
                        onChange={(values) => {
                          handleSelectChange('selectedInvestmentTypes', values);
                          field.onChange(values);
                        }}
                        colorScheme="orange"
                      />
                      <FormErrorMessage fontSize="xs">{fieldState.error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />

                <Controller
                  name="needsNonInvestmentVehicles"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
                        {t('common:nonInvestmentVehiclesAccess')}
                      </FormLabel>
                      <Box p={4} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                        <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="blue" size="md">
                          <Text fontSize="sm" color="gray.600">
                            {t('common:requestAccessToNonInvestmentVehicles')}
                          </Text>
                        </Checkbox>
                      </Box>
                    </FormControl>
                  )}
                />
              </VStack>
            </Box>
            <Flex justifyContent="space-between">
              <HexaButton
                $variant="secondary"
                className={buttonStyles.outline}
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('common:cancel')}
              </HexaButton>
              <HexaButton
                $variant="primary"
                className={buttonStyles.outline}
                onClick={handleFormSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('common:submitting') : t('common:submitAccessRequest')}
              </HexaButton>
            </Flex>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AccessRequestForm;
